// Training records (Session AFL): shared client-side types, labels and helpers for the office
// training pages AND the portal training tab. Mirrors the server map in
// server/src/modules/training/trainingTypes.ts (keep the two in step; the label strings are
// customer-facing so the house rules apply: no em dashes, no double hyphens, plain English).

export type TrainingType =
  | "HAND_WINDING"
  | "ENTRAPMENT_RELEASE"
  | "EMERGENCY_LIGHTING"
  | "FIRE_EVACUATION_LIFT_ISOLATION"
  | "OTHER";

export type TrainingSessionStatus = "SCHEDULED" | "COMPLETED" | "CANCELLED";

export const TRAINING_TYPES: TrainingType[] = [
  "HAND_WINDING",
  "ENTRAPMENT_RELEASE",
  "EMERGENCY_LIGHTING",
  "FIRE_EVACUATION_LIFT_ISOLATION",
  "OTHER",
];

export const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  HAND_WINDING: "Hand-winding procedure training",
  ENTRAPMENT_RELEASE: "Entrapment release procedure training",
  EMERGENCY_LIGHTING: "Emergency lighting training",
  FIRE_EVACUATION_LIFT_ISOLATION: "Fire evacuation and lift isolation training",
  OTHER: "On-site training",
};

export const TRAINING_STATUS_LABELS: Record<TrainingSessionStatus, string> = {
  SCHEDULED: "Scheduled",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

/** Human label for a session's training type; OTHER uses the office-typed custom label. */
export function trainingTypeLabel(type: TrainingType, customLabel?: string | null): string {
  if (type === "OTHER" && customLabel && customLabel.trim()) return customLabel.trim();
  return TRAINING_TYPE_LABELS[type] ?? TRAINING_TYPE_LABELS.OTHER;
}

export type TrainingRenewalState = "ok" | "due_soon" | "overdue";

/**
 * Renewal banding for a session's renewalDueDate: overdue when the date is past, due_soon within
 * 60 days, else ok. Date-only semantics (stored at UTC midnight), so the comparison is between
 * UTC calendar days, never raw instants.
 */
export function trainingRenewalState(
  renewalDueDate: string | Date | null | undefined,
  now: Date = new Date(),
): TrainingRenewalState | null {
  if (!renewalDueDate) return null;
  const due = typeof renewalDueDate === "string" ? new Date(renewalDueDate) : renewalDueDate;
  if (Number.isNaN(due.getTime())) return null;
  const dayMs = 86_400_000;
  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const nowDay = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (dueDay < nowDay) return "overdue";
  if (dueDay - nowDay <= 60 * dayMs) return "due_soon";
  return "ok";
}

// ---------------------------------------------------------------------------
// Office DTOs (server: server/src/modules/training/training.routes.ts)
// ---------------------------------------------------------------------------

export interface TrainingAttendeeDto {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  /** Whether a signature was captured; the raw signature data never leaves the server list DTOs. */
  hasSignature: boolean;
}

export interface TrainingLiftDto {
  assetId: string;
  eleNumber: string | null;
  liftType: string | null;
}

export interface TrainingSessionListItemDto {
  id: string;
  trainingType: TrainingType;
  customLabel: string | null;
  siteId: string;
  siteName: string;
  sessionDate: string;
  durationMinutes: number | null;
  status: TrainingSessionStatus;
  certificateRef: string | null;
  renewalDueDate: string | null;
  attendeeCount: number;
  /**
   * The engineer this session sits with, resolved server-side for display (Session AGG assignment,
   * surfaced on the LIST by Session AGM). Null means unassigned, which is a deliberate state: the
   * session is then visible to every engineer, the way an unassigned job is (Session AGN).
   *
   * On the list DTO rather than the detail DTO so the register can show it without opening a row.
   */
  assignedEngineerName: string | null;
}

export interface TrainingSessionDetailDto extends TrainingSessionListItemDto {
  deliveredBy: string | null;
  notes: string | null;
  customerNotes: string | null;
  certificateIssuedAt: string | null;
  createdAt: string;
  attendees: TrainingAttendeeDto[];
  lifts: TrainingLiftDto[];
  /** A USER id, not an Engineer id. Seeds the edit modal's picker; detail-only. */
  assignedEngineerId: string | null;
}

export interface TrainingSessionListResult {
  items: TrainingSessionListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  // Session AFN: the tenant-wide count of sessions whose two-year renewal falls within the next 60
  // days and has not yet been reminded. Drives the office "renewals due" pill (hidden when 0).
  // Always the global backlog, independent of the current filters/page.
  renewalsDueCount: number;
}

export interface TrainingAttendeeInput {
  name: string;
  role?: string | null;
  email?: string | null;
}

export interface TrainingSessionCreateInput {
  siteId: string;
  trainingType: TrainingType;
  customLabel?: string | null;
  sessionDate: string; // YYYY-MM-DD
  durationMinutes?: number | null;
  deliveredBy?: string | null;
  notes?: string | null;
  customerNotes?: string | null;
  /**
   * The engineer whose phone this session appears on (Session AGG): a USER id, from
   * api.training.engineers(). Omit or null to leave it unassigned, which makes it visible to every
   * engineer. Naming one shows it to that engineer alone.
   */
  assignedEngineerId?: string | null;
  assetIds?: string[];
  attendees?: TrainingAttendeeInput[];
}

export interface TrainingSessionUpdateInput {
  trainingType?: TrainingType;
  customLabel?: string | null;
  sessionDate?: string;
  durationMinutes?: number | null;
  deliveredBy?: string | null;
  notes?: string | null;
  customerNotes?: string | null;
  /** SCHEDULED <-> CANCELLED only; COMPLETED is reached solely via the complete endpoint. */
  status?: Exclude<TrainingSessionStatus, "COMPLETED">;
  /**
   * Reassign (or clear, with null) the engineer this session sits with. Editable at ANY status: it
   * appears nowhere on the certificate, so it is not one of the facts a completion freezes.
   */
  assignedEngineerId?: string | null;
  /** Replaces the linked lift set; accepted only while the session is SCHEDULED. */
  assetIds?: string[];
}

export interface TrainingCompleteResult {
  id: string;
  status: TrainingSessionStatus;
  certificateRef: string;
  certificateIssuedAt: string;
  renewalDueDate: string;
}

export interface TrainingListQuery {
  siteId?: string;
  status?: TrainingSessionStatus;
  type?: TrainingType;
  from?: string;
  to?: string;
  /** Session AFN: restrict to the renewal-due set (within 60 days, not yet reminded). */
  renewalDue?: boolean;
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Portal DTO (server: server/src/modules/portal/portalTraining.routes.ts).
// STRICT allow-list: never deliveredBy, never internal notes, never attendee
// names/roles/emails, never signature data.
// ---------------------------------------------------------------------------

export interface PortalTrainingSessionDto {
  id: string;
  trainingType: TrainingType;
  customLabel: string | null;
  sessionDate: string;
  durationMinutes: number | null;
  customerNotes: string | null;
  certificateRef: string | null;
  certificateIssuedAt: string | null;
  renewalDueDate: string | null;
  attendeeCount: number;
  liftEleNumbers: string[];
}

// The CROSS-SITE feed row (Session AGJ), served by GET /api/portal/training for the top-level
// Training section: the per-site DTO plus which site the session happened at. siteId is not
// decoration -- the renewal rule needs it, because a refresher at one site must not take the renewal
// prompt off an unrelated site's session of the same type (see currentRenewalSessionIds).
export interface PortalTrainingFeedSessionDto extends PortalTrainingSessionDto {
  siteId: string;
  siteName: string;
}

// ---------------------------------------------------------------------------
// Training reference documents (Session AFP). The library behind the training
// TYPES: customer-facing on-site procedures + internal-only procedures, uploaded
// to Google Drive by the office. Office DTO (server:
// server/src/modules/training/trainingRefDocs.ts) - never carries the Drive id,
// the Drive url or the uploader.
// ---------------------------------------------------------------------------

export interface TrainingReferenceDocumentDto {
  id: string;
  trainingType: TrainingType;
  trainingTypeLabel: string;
  title: string;
  version: string | null;
  /** true = shown in the portal and sendable to a customer; false = internal only. */
  customerFacing: boolean;
  mimeType: string;
  kind: string;
  uploadedAtDisplay: string;
  /** true when a newer document for the same type + audience has replaced it (history is kept). */
  superseded: boolean;
  supersededAtDisplay: string | null;
}

export interface TrainingTypeCoverage {
  customerFacing: number;
  internal: number;
}

export type TrainingCoverage = Record<TrainingType, TrainingTypeCoverage>;

// Portal DTO (server: server/src/modules/portal/portalTrainingDocs.routes.ts). STRICT allow-list:
// never customerFacing:false, never the Drive id, never the uploader.
export interface PortalTrainingDocumentDto {
  id: string;
  trainingType: TrainingType;
  trainingTypeLabel: string;
  title: string;
  version: string | null;
  kind: string;
}

export interface PortalTrainingDocGroup {
  trainingType: TrainingType;
  label: string;
  documents: PortalTrainingDocumentDto[];
}

// ---- Engineer PWA (Session AGG) ---------------------------------------------
// Server: server/src/modules/training/training.routes.ts, the /sessions/* routes. The engineer's own
// view of a training session: what to show, what to cover, who attended. Deliberately narrower than
// the office DTO -- no deliveredBy, no internal notes, and never a signature image (hasSignature is
// all that crosses).

export interface TrainingChecklistItemDto {
  id: string;
  text: string;
  // Rendered in amber beneath the point: where a person can be hurt by getting it wrong.
  warning?: string;
}

export interface EngineerTrainingAttendeeDto {
  id: string;
  name: string;
  role: string | null;
  hasSignature: boolean;
}

export interface EngineerTrainingSessionDto {
  id: string;
  trainingType: TrainingType;
  customLabel: string | null;
  // Resolved server-side (the label map has no client mirror), so one source names a training.
  typeLabel: string;
  sessionDate: string;
  durationMinutes: number | null;
  status: string;
  // False for an UNASSIGNED session, which every engineer can see and deliver (Session AGN). Not a
  // permission flag: it says whose name is on it, not whether this engineer may act on it.
  assignedToMe: boolean;
  siteName: string;
  siteAddress: string;
  sitePostcode: string | null;
  customerNotes: string | null;
  lifts: TrainingLiftDto[];
  attendeeCount: number;
  // Anything the office already entered, so the phone starts from it (the attendee write replaces).
  attendees: EngineerTrainingAttendeeDto[];
  trainingChecklist: TrainingChecklistItemDto[];
}

export interface EngineerTrainingAttendeeInput {
  // Present for a row already on record: keeps its stored signature when not re-signed.
  id?: string;
  name: string;
  role?: string | null;
  // A PNG data URL from the signing pad.
  signatureData?: string | null;
}

export interface EngineerTrainingAttendeesResult {
  /**
   * The rows as stored, in submitted order. The `id`s are NEWLY MINTED (the write replaces the list),
   * so the caller must adopt them: sending the previous ids again would reference rows this session no
   * longer has, and a signature already on record would be carried forward from nothing and lost.
   */
  attendees: Array<{ id: string; name: string; role: string | null; hasSignature: boolean }>;
}

export interface TrainingCompleteFromPwaResult {
  certificateRef: string;
  driveFileId: string | null;
  // Whether the certificate reached the Drive archive. The certificate is ISSUED either way (the
  // portal renders its own copy), so the screen must not claim it was filed when this is false.
  filed: boolean;
  warning?: "drive_not_connected" | "not_filed";
}

/**
 * An option in the office's "assign an engineer" training picker (Session AGG).
 *
 * DELIBERATELY NOT the existing `AssignableEngineer` in api.ts, which looks identical and is not:
 * that one's `id` is an ENGINEER id (call-out dispatch keys on Job.assignedTo), while this one's is
 * a USER id, because TrainingSession.assignedEngineerId points at User. Sharing the type would
 * invite passing one where the other belongs, which would silently assign nothing.
 */
export interface TrainingAssignableEngineer {
  id: string;
  name: string;
}
